import {memo, useEffect, useRef} from 'react'
import type {Virtualizer, VirtualItem} from '@tanstack/react-virtual'
import {useSelectedPathDigest, useSelectedPathDigestIsLoaded} from '../stores/selectors'

interface ScrollToSelectedDiffProps {
  virtualizer: Virtualizer<Window, Element>
  virtualItems: VirtualItem[]
  pathDigestToIndex: Map<string, number>
  diffEntryDigests: string[]
  loadManyPriority: (digests: string[]) => void
}

/**
 * Isolated component that handles scrolling to the selected diff entry.
 *
 * This is extracted from PullRequestVirtualizedDiffsList so that subscribing
 * to `selectedPathDigest` (which changes on every file tree click) does NOT
 * trigger a re-render of the entire virtualized diff list. Only this small
 * component re-renders when the selection changes, keeping the scroll-to
 * logic reactive without the cost of re-rendering all visible diff rows.
 */
// eslint-disable-next-line @typescript-eslint/no-shadow -- named function expression intentionally shares the outer name
export const ScrollToSelectedDiff = memo(function ScrollToSelectedDiff({
  virtualizer,
  virtualItems,
  pathDigestToIndex,
  diffEntryDigests,
  loadManyPriority,
}: ScrollToSelectedDiffProps) {
  const selectedPathDigest = useSelectedPathDigest()

  // Subscribe reactively to the selected entry's isLoaded status so that
  // when the diff data finishes loading, this component re-renders and
  // the scroll effect can fire. Without this subscription, the component
  // would never know when an unloaded diff (outside the overscan window)
  // finishes loading.
  const selectedPathDigestIsLoaded = useSelectedPathDigestIsLoaded()

  // Cache the path we've already scrolled to so we do not keep snapping back while the user scrolls
  const hasScrolledToSelectionRef = useRef<string | null>(null)

  useEffect(() => {
    if (!selectedPathDigest) {
      hasScrolledToSelectionRef.current = null
      return
    }

    if (hasScrolledToSelectionRef.current === selectedPathDigest) {
      return
    }

    const targetIndex = pathDigestToIndex.get(selectedPathDigest)
    if (targetIndex === undefined) return

    if (!selectedPathDigestIsLoaded) {
      // load the diffs before the target to reduce diffs moving after they load
      loadManyPriority(
        [diffEntryDigests[targetIndex - 2], diffEntryDigests[targetIndex - 1], diffEntryDigests[targetIndex]].filter(
          digest => digest !== undefined,
        ),
      )
      return
    }

    // Check if the target diff is actually visible in the natural viewport range.
    // We can't just check virtualItems because the rangeExtractor forcibly adds selected items
    // to keep them in the DOM (for accessibility). Instead, check if the item's position
    // falls within the current scroll viewport.
    const virtualItem = virtualItems.find(item => item.index === targetIndex)
    const isActuallyInViewport =
      virtualItem &&
      virtualItem.start >= (virtualizer.scrollOffset ?? 0) - virtualItem.size &&
      virtualItem.start <= (virtualizer.scrollOffset ?? 0) + (virtualizer.scrollRect?.height ?? 0)

    if (isActuallyInViewport) {
      hasScrolledToSelectionRef.current = selectedPathDigest
      return
    }

    requestAnimationFrame(() => {
      virtualizer.scrollToIndex(targetIndex, {align: 'start'})
    })

    hasScrolledToSelectionRef.current = selectedPathDigest
  }, [
    selectedPathDigest,
    pathDigestToIndex,
    virtualizer,
    virtualItems,
    selectedPathDigestIsLoaded,
    loadManyPriority,
    diffEntryDigests,
  ])

  return null
})
